import React, { Fragment, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { appConfig } from 'config/app';
import { AuthService } from 'lib';
import { PermissionGate } from 'screens/Security';
import { ICurrentEmployee, IEmployerState } from 'store/types';
import { EmployeeRole, EmployeeType } from 'types';
import { ActionMenuList, ActionMenuSection } from '../../ActionMenu';
import { DropdownDismiss, HeaderDropdownItem, HeaderDropdownLayout, NavItems, RoleItem, RoleItemWrap, RoleList, RoleListTitle, RoleOptionsDesktop, RoleOptionsList, RoleOptionsMobile, SelectableItem } from './styles';

const WIDTH_PARENT = 200;
const WIDTH_CHILD = 275;

interface IHeaderDropdownProps {
  adminRoleOrgs: IEmployerState[];
  className?: string;
  managerRoleOrgs: any[];
  currentOption?: ICurrentEmployee;
  isVisible?: boolean;
  onClose?: () => void;
  onSettings?: () => void;
  onSelect: (option: ICurrentEmployee) => void;
  onSelectSelf: () => void;
  onLogout?: () => void;
  username: string;
}

type MenuType = string | undefined;

const HeaderDropdown = ({ adminRoleOrgs, className, currentOption, isVisible = true, managerRoleOrgs, onSelectSelf, onClose, onSelect, onSettings, onLogout, username }: IHeaderDropdownProps) => {
  const [childMenu, setChildMenu] = useState<MenuType>(undefined);

  const removeChildMenu = () => {
    setMenu(undefined);
    setChildMenu(undefined);
  };

  const handleClose = () => {
    removeChildMenu();
    onClose?.();
  };

  const profiles = [
    ...(adminRoleOrgs.length >= 1
      ? [
          {
            label: 'Org Admin',
            listTitle: 'Organizations',
            options: adminRoleOrgs.map((org: { id: string; type: EmployeeType; name: string; sites: any }) => ({
              id: org.id,
              role: org.type,
              name: org.name,
              type: EmployeeType.ADMIN,
              empId: org.sites[Object.keys(org.sites)[0]]?.employee?.id,
            })),
          },
        ]
      : []),
    ...(managerRoleOrgs.length >= 1
      ? [
          {
            label: 'Site Admin / Manager',
            listTitle: 'Sites',
            options: managerRoleOrgs.map((site: { id: string; organizationId: string; role: EmployeeRole; name: string; address: string; employee?: { id: string } }) => ({
              id: site.organizationId,
              siteId: site.id,
              role: site.role,
              name: site.name,
              address: site.address,
              type: EmployeeType.USER,
              empId: site.employee?.id,
            })),
          },
        ]
      : []),
  ];

  const setMenu = (menu: MenuType) => {
    if (childMenu === undefined || childMenu !== menu) {
      setChildMenu(menu);
    } else {
      setChildMenu(undefined);
    }
  };

  const handleSelect = ({ id, siteId, empId }: { id: string; siteId: string; empId: string }) => {
    onSelect?.({ id: empId, organizationId: id, siteId });
    handleClose();
  };

  const handleSelectSelf = () => {
    onSelectSelf?.();
    handleClose();
  };

  const isSalesforceSupport = process.env.REACT_APP_SALESFORCE_SUPPORT;
  const isSalesforceSSOBaseUrl = process.env.REACT_APP_SALESFORCE_SSO_BASE_URL;
  const jwt = AuthService.getToken();

  return (
    <>
      <HeaderDropdownLayout width={childMenu !== undefined ? WIDTH_PARENT + WIDTH_CHILD : WIDTH_PARENT} isVisible={isVisible} onClose={handleClose} className={className} data-cy="profile-switcher-dropdown">
        <ActionMenuSection width={WIDTH_PARENT}>
          {profiles && profiles.length >= 1 && (
            <RoleList>
              <RoleListTitle>Use Kickfin as</RoleListTitle>

              {profiles.map((profile: any, index) => {
                const isActive = childMenu === profile.label;
                return (
                  <Fragment key={index}>
                    <RoleItem data-cy="profile-switcher-role-option" label={profile.label} onClick={() => setMenu(profile.label)} isActive={isActive} />

                    <RoleOptionsMobile isActive={isActive}>
                      {
                        // mobile accordion options
                        isActive && (
                          <RoleOptionsList>
                            <RoleListTitle type="child">{profile.listTitle}</RoleListTitle>

                            {sortBy(profile.options, 'name').map((option: any) => (
                              <SelectableItem
                                key={option.name}
                                label={option.name}
                                description={option.address}
                                onClick={() => handleSelect(option)}
                                isActive={option.siteId ? option.siteId === currentOption?.siteId : option.id === currentOption?.organizationId}
                              />
                            ))}
                          </RoleOptionsList>
                        )
                      }
                    </RoleOptionsMobile>
                  </Fragment>
                );
              })}

              <PermissionGate requiredRoles={[]} remoteSession={false}>
                <RoleItemWrap>
                  <SelectableItem data-cy="profile-switcher-no-role-item" isActive={!currentOption} onClick={handleSelectSelf} label={username} />
                </RoleItemWrap>
              </PermissionGate>
            </RoleList>
          )}

          <NavItems listType="parent">
            <PermissionGate requiredRoles={[]} remoteSession={false}>
              <HeaderDropdownItem onClick={onSettings} data-cy="settings-item">
                Settings
              </HeaderDropdownItem>
            </PermissionGate>

            {isSalesforceSupport && isSalesforceSSOBaseUrl && jwt ? (
              <HeaderDropdownItem as="a" href={`${isSalesforceSSOBaseUrl}?jwt=${jwt}`} rel="noopener noreferrer" target="_blank" data-cy="support-item">
                Support
              </HeaderDropdownItem>
            ) : (
              <HeaderDropdownItem as="a" href={appConfig.LINKS.SUPPORT} rel="noopener noreferrer" target="_blank" data-cy="support-item">
                Support
              </HeaderDropdownItem>
            )}

            <HeaderDropdownItem onClick={onLogout} data-cy="logout-item">
              Log Out
            </HeaderDropdownItem>
          </NavItems>
        </ActionMenuSection>

        {
          // flyout menu on desktop
          profiles &&
            profiles.map((profile: any, index) => (
              <Fragment key={index}>
                {childMenu === profile.label && (
                  <RoleOptionsDesktop width={WIDTH_CHILD} childLevel childLevelLeft={WIDTH_PARENT} data-cy="role-options">
                    <ActionMenuList listType="child" title={profile.listTitle}>
                      {sortBy(profile.options, 'name').map((option) => (
                        <SelectableItem
                          key={option.name}
                          label={option.name}
                          description={option.address}
                          data-cy="organization-option"
                          onClick={() => handleSelect(option)}
                          isActive={option.siteId ? option.siteId === currentOption?.siteId : option.id === currentOption?.organizationId}
                        />
                      ))}
                    </ActionMenuList>
                  </RoleOptionsDesktop>
                )}
              </Fragment>
            ))
        }
      </HeaderDropdownLayout>

      {isVisible && <DropdownDismiss onClick={handleClose} />}
    </>
  );
};

export default HeaderDropdown;
